import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./HomePage.css";
import "./StoreSlugEditor.css";
import "./button-styles.css";
import LogoImg from "../assets/layerbound-white.png";
import { signOut, fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { resolveAuthToken } from "../utils/authToken";
import { useNavigate, Link } from "react-router-dom";
import { usePageMetadata } from "../hooks/usePageMetadata";
import {
  QrCode,
  MessageCircle,
  X,
  TrendingUp,
  Package,
  AlertTriangle,
  Zap,
  Edit3,
  BarChart3,
  CheckCircle,
  Copy,
  ExternalLink,
  Sparkles,
  Clock,
  Store,
  Trash2,
  KeyRound,
} from "lucide-react";
import awsConfig from "../aws-config";
import { resolveMenuBaseUrl, getMenuBaseDisplay } from "../utils/menuLinks";
import { formatCurrency as formatCurrencyValue } from "../utils/currency";
import UpgradePlanSelector from "../components/UpgradePlanSelector";
import { useLifetimeUpgradePlans } from "../hooks/useLifetimeUpgradePlans";
import { getPlanById } from "../constants/upgradePlans";
import CallReadinessModal from "../components/CallReadinessModal";
import { getCallReadyStats } from "../utils/leadReadiness";

const BUSINESS_API_URL = awsConfig.api_url_create_business;
const UPGRADE_API_URL = awsConfig.api_url_upgrade;
const SLUG_API_URL = awsConfig.api_url_manage_slug;
const MENU_BASE_URL = resolveMenuBaseUrl(awsConfig.menu_base_url);
const MENU_BASE_DISPLAY = getMenuBaseDisplay(awsConfig.menu_base_url);
const PENDING_STATUSES = new Set(["PENDING", "PENDING_CASH"]);
const BOOKING_DATE_FORMAT = { month: "short", day: "numeric", year: "numeric" };
const BOOKING_TIME_FORMAT = { hour: "2-digit", minute: "2-digit" };

const parseSlotDate = (slot) => {
  if (!slot) return null;
  const fromIso = slot.startLocal || slot.startUtc;
  if (fromIso) {
    const parsed = new Date(fromIso);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  if (slot.slotDate && slot.startTime) {
    const parsed = new Date(`${slot.slotDate}T${slot.startTime}`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  if (slot.slotDate) {
    const parsed = new Date(slot.slotDate);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

const sanitizeSlugFragment = (value) => {
  if (!value) return "";
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

const getBusinessSlug = (business) => {
  if (!business) return "";
  const existing = sanitizeSlugFragment(business.slug);
  if (existing) return existing;
  const fromName = sanitizeSlugFragment(business.name);
  if (fromName) return fromName;
  const id = (business?.businessId || "").toLowerCase().trim();
  if (!id) return "biz-index";
  return `biz-${id.slice(0, 10)}`;
};

function HomePage({ user, onSignOut }) {
  usePageMetadata({
    title: "Home",
    description:
      "Review your Layerbound AI systems, orders, and plan details in one place.",
  });
  const [business, setBusiness] = useState(null);
  const [copied, setCopied] = useState(false);
  const [orders, setOrders] = useState([]);
  const leads = useMemo(
    () => (orders || []).filter((o) => (o.type || "").toLowerCase() === "lead"),
    [orders]
  );
  const realOrders = useMemo(
    () => (orders || []).filter((o) => (o.type || "").toLowerCase() !== "lead"),
    [orders]
  );
  const [leadFilter, setLeadFilter] = useState({ store: "all", source: "all" });
  const [orderFilter, setOrderFilter] = useState({ store: "all" });
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showCallReadiness, setShowCallReadiness] = useState(false);
  const [callReadinessFilter, setCallReadinessFilter] = useState("all");
  const [copiedSummaryId, setCopiedSummaryId] = useState(null);
  const [showArchivedLeads, setShowArchivedLeads] = useState(false);
  const [menuPublishes, setMenuPublishes] = useState({ active: [], archived: [] });
  const [upgrading, setUpgrading] = useState(false);
  const [showAllTimeStats, setShowAllTimeStats] = useState(false);
  const [showSlugEdit, setShowSlugEdit] = useState(false);
  const activePlanId = useMemo(() => (business?.plan || "free").toLowerCase(), [business?.plan]);
  const activePlan = useMemo(() => getPlanById(activePlanId), [activePlanId]);
  const planBadge = useMemo(() => {
    if (!business) return null;
    if (activePlanId === "enterprise" || activePlanId === "pro" || activePlanId === "scale") {
      return { className: "plan-badge enterprise", label: "Enterprise Plan", icon: <Sparkles size={12} /> };
    }
    if (activePlanId === "starter") {
      return { className: "plan-badge starter", label: "Starter Plan", icon: <Zap size={12} /> };
    }
    if (activePlanId.startsWith("lifetime")) {
      return {
        className: "plan-badge lifetime",
        label: activePlan?.name || "Lifetime Plan",
        icon: <Sparkles size={12} />,
      };
    }
    return { className: "plan-badge free", label: "Free Plan" };
  }, [business, activePlanId, activePlan]);
  const [newSlug, setNewSlug] = useState('');
  const [slugLoading, setSlugLoading] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [deletingPublish, setDeletingPublish] = useState(null);
  const [editingStore, setEditingStore] = useState(null);
  const [editingBookingStore, setEditingBookingStore] = useState(null);
  const [bookingDraft, setBookingDraft] = useState({
    resourceLabel: "",
    slotDurationMinutes: 60,
    maxSimultaneousBookings: 1,
    startTime: "09:00",
    endTime: "17:00",
    timezone: "UTC",
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    notes: "",
  });
  const [bookingError, setBookingError] = useState("");
  const [savingBooking, setSavingBooking] = useState(false);
  const [newStoreSlug, setNewStoreSlug] = useState('');
  const [pendingStoreDeletion, setPendingStoreDeletion] = useState(null);
  const [deleteStoreError, setDeleteStoreError] = useState('');
  const deriveStoreUrl = useCallback(
    (storeSlug) => {
      const bizSlug = getBusinessSlug(business);
      const normalizedStoreSlug = sanitizeSlugFragment(storeSlug) || "index";
      if (!bizSlug || !normalizedStoreSlug) return "";
      return `${MENU_BASE_URL}/${bizSlug}/${normalizedStoreSlug}`;
    },
    [business]
  );
  const storeLookup = useMemo(() => {
    const map = {};
    (menuPublishes.active || []).forEach((pub) => {
      const storeId = pub.storeId || pub.store_id;
      const slug = pub.storeSlug || pub.slug;
      if (storeId) map[`id:${storeId}`] = pub;
      if (slug) map[`slug:${slug}`] = pub;
    });
    return map;
  }, [menuPublishes.active]);
  const resolveStoreMeta = useCallback(
    (entry) => {
      const payload = entry?.lead || entry || {};
      const storeId =
        payload.storeId || payload.store_id || entry?.storeId || entry?.store_id || "";
      const rawSlug =
        payload.storeSlug ||
        payload.store_slug ||
        payload.store ||
        entry?.storeSlug ||
        entry?.store_slug ||
        entry?.store ||
        "";
      const slug = rawSlug || storeId;
      const storeName =
        payload.storeName ||
        payload.store_name ||
        entry?.storeName ||
        entry?.store_name ||
        "";
      const directUrl =
        payload.publicUrl ||
        payload.storeUrl ||
        payload.store_url ||
        payload.intakeUrl ||
        payload.intake_url ||
        entry?.publicUrl ||
        entry?.storeUrl ||
        entry?.store_url ||
        entry?.intakeUrl ||
        entry?.intake_url ||
        "";
      const pub = storeLookup[`id:${storeId}`] || storeLookup[`slug:${slug}`];
      const hasStoreRef = Boolean(storeName || slug || storeId);
      const name =
        pub?.storeName ||
        pub?.name ||
        storeName ||
        slug ||
        storeId ||
        (hasStoreRef ? "Store" : "Link not found");
      const url =
        directUrl ||
        pub?.publicUrl ||
        (slug ? deriveStoreUrl(slug) : "");
      return { name, url, slug: slug || pub?.storeSlug || "", storeId };
    },
    [storeLookup, deriveStoreUrl]
  );

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const isArchived = Boolean(lead.archived || lead.lead?.archived);
      if (!showArchivedLeads && isArchived) return false;
      const storeMeta = resolveStoreMeta(lead);
      const source = (lead.lead?.source || lead.source || "storefront").toLowerCase();

      const storeMatch =
        leadFilter.store === "all"
          ? true
          : leadFilter.store === "unknown"
          ? !storeMeta.storeId && !storeMeta.slug
          : storeMeta.storeId === leadFilter.store || storeMeta.slug === leadFilter.store;

      const sourceMatch =
        leadFilter.source === "all"
          ? true
          : leadFilter.source === "unknown"
          ? !source
          : source === leadFilter.source;

      return storeMatch && sourceMatch;
    });
  }, [leads, leadFilter, resolveStoreMeta, showArchivedLeads]);

  const resolveDrr = useCallback((lead) => lead?.drr || lead?.lead?.drr || null, []);

  const normalizeFieldList = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => ({
        label: (item?.label || "").trim(),
        value: (item?.value || "").trim(),
      }))
      .filter((item) => item.label && item.value);
  }, []);

  const parseRawItems = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => {
        const text = String(item || "").trim();
        if (!text) return null;
        const splitIndex = text.indexOf(":");
        if (splitIndex === -1) {
          return { label: "Detail", value: text };
        }
        const label = text.slice(0, splitIndex).trim().replace(/\?$/, "");
        const value = text.slice(splitIndex + 1).trim();
        if (!label || !value) return null;
        return { label, value };
      })
      .filter(Boolean);
  }, []);

  const getFieldByLabel = useCallback((items, label) => {
    if (!label) return null;
    const target = label.toLowerCase();
    return (items || []).find((item) => item.label.toLowerCase() === target) || null;
  }, []);

  const getRoleTitle = useCallback((drr, storeMeta) => {
    return (
      drr?.role_title ||
      (storeMeta?.name && storeMeta.name !== "Store" ? storeMeta.name : "") ||
      "the role"
    );
  }, []);

  const getSignatureLines = useCallback(() => {
    const lines = [];
    if (user?.username) lines.push(user.username);
    if (business?.name) lines.push(business.name);
    if (business?.phone) lines.push(business.phone);
    return lines.length ? lines : ["Recruiter"];
  }, [user, business]);

  const buildClientSummary = useCallback(
    ({ name, storeMeta, drr }) => {
      const summaryLines = [];
      const candidateName = name || "Candidate";
      const roleName = getRoleTitle(drr, storeMeta);
      const intakeProfile = drr?.intake_profile || null;

      if (intakeProfile) {
        const missingInfo = Array.isArray(intakeProfile.missing_info)
          ? intakeProfile.missing_info.filter((item) => String(item || "").trim())
          : [];
        const keyDetails = [
          ["Route", intakeProfile.route],
          ["Company", intakeProfile.company],
          ["People count", intakeProfile.people_count],
          ["Start timing", intakeProfile.start_timing],
          ["Preferred contact", intakeProfile.preferred_contact],
          ["Brief details", intakeProfile.enquiry_details],
        ].filter(([, value]) => String(value || "").trim());

        summaryLines.push("Hi Team,");
        summaryLines.push("");
        summaryLines.push(`New programme enquiry for ${roleName} (${candidateName}).`);
        summaryLines.push(
          `${intakeProfile.review_status || "Needs review"} with the key follow-up details below.`
        );

        if (keyDetails.length) {
          summaryLines.push("");
          summaryLines.push("Key details");
          keyDetails.forEach(([label, value]) => summaryLines.push(`• ${label}: ${value}`));
        }

        if (missingInfo.length) {
          summaryLines.push("");
          summaryLines.push("Missing details");
          missingInfo.forEach((item) => summaryLines.push(`• ${item}`));
        }

        if ((drr?.potential_blockers || []).length) {
          summaryLines.push("");
          summaryLines.push("Potential blockers");
          drr.potential_blockers.forEach((item) => summaryLines.push(`• ${item}`));
        }

        summaryLines.push("");
        summaryLines.push("Please review the enquiry and confirm the best next step.");
        summaryLines.push("");
        summaryLines.push("Kind regards,");
        summaryLines.push(...getSignatureLines().filter((line) => !/^google_/i.test(line)));

        return summaryLines.join("\n");
      }

      summaryLines.push("Hi Team,");
      summaryLines.push("");
      summaryLines.push(
        `Programme enquiry summary for ${roleName} (${candidateName}).`
      );
      summaryLines.push("Short follow-up summary with routing, timing, and key context below.");
      const fields = drr?.fields || {};
      let constraintFields = normalizeFieldList(fields.constraints);
      let availabilityFields = normalizeFieldList(fields.availability_compensation);
      let experienceFields = normalizeFieldList(fields.relevant_experience);
      let otherFields = normalizeFieldList(fields.other);

      const hasStructured =
        constraintFields.length ||
        availabilityFields.length ||
        experienceFields.length ||
        otherFields.length;

      if (!hasStructured) {
        constraintFields = parseRawItems(drr?.key_constraints);
        availabilityFields = parseRawItems(drr?.availability_compensation);
        experienceFields = parseRawItems(drr?.relevant_experience);
        otherFields = parseRawItems(drr?.potential_blockers);
      }

      const keyDetails = [];
      const usedKeys = new Set();
      const fieldKey = (item) => `${item.label}|${item.value}`;
      const addUsed = (item) => item && usedKeys.add(fieldKey(item));

      const location = getFieldByLabel(constraintFields, "Location");
      const relocation = getFieldByLabel(constraintFields, "Relocation");
      if (location) {
        let line = `Based in ${location.value}`;
        if (relocation) {
          const relValue = relocation.value.toLowerCase();
          const relText = relValue.includes("open") || relValue.includes("willing")
            ? "open to relocation"
            : relocation.value;
          line = `${line} (${relText})`;
        }
        keyDetails.push(line);
        addUsed(location);
        addUsed(relocation);
      }

      const rightToWork = getFieldByLabel(constraintFields, "Right to work");
      if (rightToWork) {
        keyDetails.push(`Right to work: ${rightToWork.value}`);
        addUsed(rightToWork);
      }

      const availability = getFieldByLabel(availabilityFields, "Availability");
      if (availability) {
        keyDetails.push(`Availability: ${availability.value}`);
        addUsed(availability);
      }

      const salary = getFieldByLabel(availabilityFields, "Salary expectation");
      if (salary) {
        keyDetails.push(`Salary expectation: ${salary.value}`);
        addUsed(salary);
      }

      const workPref = getFieldByLabel(availabilityFields, "Work preference");
      if (workPref) {
        keyDetails.push(`Work preference: ${workPref.value}`);
        addUsed(workPref);
      }

      constraintFields.forEach((item) => {
        if (!usedKeys.has(fieldKey(item))) {
          keyDetails.push(`${item.label}: ${item.value}`);
        }
      });
      availabilityFields.forEach((item) => {
        if (!usedKeys.has(fieldKey(item))) {
          keyDetails.push(`${item.label}: ${item.value}`);
        }
      });

      const experience = experienceFields.map((item) => {
        if (item.label.toLowerCase() === "experience") {
          return /experience/i.test(item.value)
            ? item.value
            : `${item.value} experience`;
        }
        return `${item.label}: ${item.value}`;
      });

      const notes = otherFields.filter((item) => item.label.toLowerCase() !== "role title");

      if (keyDetails.length) {
        summaryLines.push("");
        summaryLines.push("Key details");
        keyDetails.forEach((item) => summaryLines.push(`• ${item}`));
      }

      if (experience.length) {
        summaryLines.push("");
        summaryLines.push("Experience");
        experience.forEach((item) => summaryLines.push(`• ${item}`));
      }

      if (notes.length) {
        summaryLines.push("");
        summaryLines.push("Additional notes");
        notes.forEach((item) => summaryLines.push(`• ${item.label}: ${item.value}`));
      }

      summaryLines.push("");
      summaryLines.push("Please let me know if you'd like me to arrange the next step or request any more detail.");
      summaryLines.push("");
      summaryLines.push("Kind regards,");
      summaryLines.push(...getSignatureLines().filter((line) => !/^google_/i.test(line)));

      return summaryLines.join("\n");
    },
    [getRoleTitle, normalizeFieldList, parseRawItems, getFieldByLabel, getSignatureLines]
  );

  const buildCandidateSummary = useCallback(
    ({ name, storeMeta, drr }) => {
      const summaryLines = [];
      const candidateName = name || "there";
      const intakeProfile = drr?.intake_profile || null;

      summaryLines.push(`Hi ${candidateName},`);
      summaryLines.push("");
      summaryLines.push(
        intakeProfile
          ? "Thanks for sending your enquiry."
          : "Thanks for completing the apprenticeship enquiry form."
      );
      summaryLines.push("We’ll review your enquiry and confirm the best next step shortly.");
      summaryLines.push("");
      summaryLines.push("Speak soon,");
      summaryLines.push(...getSignatureLines().filter((line) => !/^google_/i.test(line)));

      return summaryLines.join("\n");
    },
    [getSignatureLines]
  );

  const handleCopySummary = useCallback((id, text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopiedSummaryId(id);
    window.setTimeout(() => setCopiedSummaryId(null), 1500);
  }, []);

  const callReadyLeads = useMemo(() => {
    return filteredLeads.filter((lead) => {
      const drr = resolveDrr(lead);
      const qualificationCount = (lead.lead?.qualification || lead.qualification || []).length;
      const minRequired =
        Number(lead.lead?.minRequiredQuestions || lead.minRequiredQuestions || 2) || 2;
      return drr && drr.is_sufficient && qualificationCount >= minRequired;
    });
  }, [filteredLeads, resolveDrr]);

  const callReadyStats = useMemo(
    () => getCallReadyStats(callReadyLeads, resolveDrr),
    [callReadyLeads, resolveDrr]
  );

  const filteredOrders = useMemo(() => {
    if (orderFilter.store === "all") return realOrders;
    if (orderFilter.store === "unknown") {
      return realOrders.filter((o) => {
        const meta = resolveStoreMeta(o);
        return !meta.storeId && !meta.slug;
      });
    }
    return realOrders.filter((o) => {
      const meta = resolveStoreMeta(o);
      return meta.storeId === orderFilter.store || meta.slug === orderFilter.store;
    });
  }, [orderFilter.store, realOrders, resolveStoreMeta]);
  const {
    saleWindow,
    countdownLabel,
    availablePlans,
    selectedPlanId,
    setSelectedPlanId,
    selectedPlan,
  } = useLifetimeUpgradePlans();
  const saleStatusCopy = useMemo(() => {
    if (saleWindow.phase === "active") {
      return countdownLabel
        ? `Black Friday Lifetime Deal is live – ${countdownLabel} left.`
        : "Black Friday Lifetime Deal is live.";
    }
    if (saleWindow.phase === "upcoming") {
      const startLabel = saleWindow.start
        ? saleWindow.start.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : null;
      return startLabel ? `Lifetime Deal unlocks ${startLabel}.` : "Lifetime Deal unlocks soon.";
    }
    if (saleWindow.phase === "ended") {
      return "Lifetime Deal has ended. Starter plan is still available monthly.";
    }
    return "Upgrade unlocks more orders, stores, and white-label controls.";
  }, [saleWindow.phase, saleWindow.start, countdownLabel]);
  const currencyCode = (business?.currencyCode || "GBP").toUpperCase();
  const formatAmount = useCallback(
    (value) => formatCurrencyValue(value, currencyCode),
    [currencyCode]
  );
  const formatSlotWhen = useCallback((slot) => {
    const parsed = parseSlotDate(slot);
    const dateLabel = parsed ? parsed.toLocaleDateString(undefined, BOOKING_DATE_FORMAT) : slot?.slotDate;
    const timeLabel = parsed ? parsed.toLocaleTimeString([], BOOKING_TIME_FORMAT) : slot?.startTime || "";
    if (dateLabel && timeLabel) return `${dateLabel} · ${timeLabel}`;
    return dateLabel || timeLabel || "";
  }, []);
  const formatBookingDetails = useCallback(
    (order) => {
      const slots = Array.isArray(order?.slotReservations) ? order.slotReservations : [];
      return slots.map((slot, idx) => {
        const when = formatSlotWhen(slot);
        const label = slot?.label || slot?.slotLabel || slot?.resourceLabel || "Booking";
        const qty = slot?.qty ? ` x${slot.qty}` : "";
        const resource = slot?.resourceLabel && slot.resourceLabel !== label ? ` · ${slot.resourceLabel}` : "";
        const id = slot?.slotId || slot?.slot_id || `${label}-${idx}`;
        return { id, text: `${label}${qty}${when ? ` — ${when}` : ""}${resource}` };
      });
    },
    [formatSlotWhen]
  );
  const formatItemsList = useCallback((order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    if (!items.length) return "—";
    return items
      .map((i, idx) => `${i.name || `Item ${idx + 1}`} x${i.qty ?? 1}`)
      .join(", ");
  }, []);
  const navigate = useNavigate();

  const derivedBusinessSlug = useMemo(() => getBusinessSlug(business), [business]);

  useEffect(() => {
    if (!showSlugEdit && derivedBusinessSlug) {
      setNewSlug(derivedBusinessSlug);
    }
  }, [derivedBusinessSlug, showSlugEdit]);

  const getAuthToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = resolveAuthToken(session);
    if (!token) throw new Error("Not authenticated");
    return token;
  }, []);

  const fetchMenuPublishes = useCallback(async () => {
    if (!business?.businessId) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${awsConfig.api_url_get_menu}/${business.businessId}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      if (!res.ok) throw new Error("Failed to fetch menu publishes");
      const data = await res.json();

      // Make sure we're properly separating active from archived stores
      const publishes = data.publishes || { active: [], archived: [] };

      // Filter out any duplicate stores that might appear in both active and archived
      const activeIds = new Set(publishes.active.map((p) => p.publishedAt));
      const filteredArchived = publishes.archived.filter((p) => !activeIds.has(p.publishedAt));

      setMenuPublishes({
        active: publishes.active,
        archived: filteredArchived,
      });
      if (data.business?.slug) {
        setBusiness((prev) => {
          if (!prev) return prev;
          if (prev.slug === data.business.slug) return prev;
          return { ...prev, slug: data.business.slug };
        });
      }
    } catch (err) {
      console.error("Error fetching menu publishes", err);
    }
  }, [business?.businessId, getAuthToken]);

  // ===== Fetch Business Info =====
  useEffect(() => {
    const fetchBusiness = async () => {
      setLoadingBusiness(true);
      try {
        const userObj = await getCurrentUser().catch(() => null);
        if (!userObj) {
          navigate("/auth");
          return;
        }
        const token = await getAuthToken();
        const res = await fetch(BUSINESS_API_URL, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch business info");
        const data = await res.json();
        const normalizedSlug = getBusinessSlug(data);
        setBusiness({ ...data, slug: normalizedSlug });
        setNewSlug(normalizedSlug);
      } catch (err) {
        setBusiness(null);
        console.error("Error fetching business info", err);
      } finally {
        setLoadingBusiness(false);
      }
    };
    fetchBusiness();
  }, [getAuthToken, navigate]);

  // ===== Fetch Menu Publishes =====
  useEffect(() => {
    if (business?.businessId) fetchMenuPublishes();
  }, [business?.businessId, fetchMenuPublishes]);

  const paymentsEnabled = business?.stripeConnected ?? false;

  // ===== Fetch All Orders =====
  useEffect(() => {
    const fetchOrders = async () => {
      if (!business?.businessId) return;
      setLoadingOrders(true);
      try {
        const token = await getAuthToken();
        const res = await fetch(
          `${awsConfig.api_url_get_order}?businessId=${business.businessId}&status=ALL`,
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );
        if (!res.ok) throw new Error("Failed to fetch orders");
        const data = await res.json();
        setOrders(data.orders || []);
      } catch (err) {
        console.error("Error fetching orders", err);
      } finally {
        setLoadingOrders(false);
      }
    };
    if (business?.businessId) fetchOrders();
  }, [business?.businessId, getAuthToken]);

  // ===== Monthly Usage Calculation =====
  const calculateMonthlyUsage = () => {
    if (!realOrders.length) return { monthlyOrdersUsed: 0, monthlyRevenueUsed: 0 };
    
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const monthlyOrders = realOrders.filter(order => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= monthStart && 
             ['ACCEPTED', 'PAID', 'COMPLETED'].includes(order.status);
    });
    
    const monthlyOrdersUsed = monthlyOrders.length;
    const monthlyRevenueUsed = monthlyOrders.reduce(
      (sum, order) => sum + Number(order.total || 0), 
      0
    );
    
    return { monthlyOrdersUsed, monthlyRevenueUsed };
  };

  // ===== Slug Management =====
  const handleSlugUpdate = async () => {
    if (!business?.businessId || !newSlug.trim()) return;
    
    setSlugLoading(true);
    setSlugError('');
    
    try {
      const token = await getAuthToken();
      
      const res = await fetch(SLUG_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: business.businessId,
          slug: newSlug.trim().toLowerCase()
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update slug');
      }
      
      // Update business state
      setBusiness(prev => ({ ...prev, slug: data.newSlug }));
      setShowSlugEdit(false);
      
      // Refetch menu publishes to get updated URLs
      const publishRes = await fetch(
        `${awsConfig.api_url_get_menu}/${business.businessId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (publishRes.ok) {
        const publishData = await publishRes.json();
        setMenuPublishes(publishData.publishes || { active: [], archived: [] });
      }
      
      // Show success animation
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
    } catch (err) {
      setSlugError(err.message);
    } finally {
      setSlugLoading(false);
    }
  };

  // ===== Delete Publish =====
  const handleDeletePublish = async (publishedAt) => {
    if (!business?.businessId) return false;
    
    setDeletingPublish(publishedAt);
    setDeleteStoreError('');
    let success = false;
    
    try {
      const token = await getAuthToken();
      
      const res = await fetch(
        `${awsConfig.api_url_delete_menu}/${business.businessId}/${publishedAt}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!res.ok) {
        throw new Error('Failed to delete menu. Please try again.');
      }
      
      // Get the store that's being deleted so we can move it to archived
      const storeToArchive = menuPublishes.active.find(p => p.publishedAt === publishedAt);
      
      // Update local state - both remove from active AND add to archived
      setMenuPublishes(prev => ({
        active: prev.active.filter(p => p.publishedAt !== publishedAt),
        archived: storeToArchive ? [...prev.archived, {...storeToArchive, status: 'archived'}] : prev.archived
      }));
      
      // Force refetch menu publishes to ensure sync with backend
      const refreshRes = await fetch(
        `${awsConfig.api_url_get_menu}/${business.businessId}`,
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        setMenuPublishes(data.publishes || { active: [], archived: [] });
      }
      
      success = true;
    } catch (err) {
      console.error('Error deleting publish:', err);
      setDeleteStoreError(err.message || 'Failed to delete menu. Please try again.');
    } finally {
      setDeletingPublish(null);
    }
    
    return success;
  };

  const confirmStoreDeletion = async () => {
    if (!pendingStoreDeletion) return;
    const success = await handleDeletePublish(pendingStoreDeletion.publishedAt);
    if (success) {
      setPendingStoreDeletion(null);
      setDeleteStoreError('');
    }
  };

  const openBookingModal = async (pub) => {
    setEditingBookingStore(pub);
    setBookingError("");
    const bs = pub.bookingSettings || pub.booking_settings || {};
    setBookingDraft({
      resourceLabel: bs.resourceLabel || "",
      slotDurationMinutes: bs.slotDurationMinutes || 60,
      maxSimultaneousBookings: bs.maxSimultaneousBookings || 1,
      startTime: bs.startTime || bs.dailyStartTime || "09:00",
      endTime: bs.endTime || bs.dailyEndTime || "17:00",
      timezone: bs.timezone || bs.timeZone || business?.timeZone || "UTC",
      daysOfWeek: Array.isArray(bs.daysOfWeek) && bs.daysOfWeek.length ? bs.daysOfWeek : [0, 1, 2, 3, 4, 5, 6],
      notes: bs.notes || "",
    });
    // Per-schedule fields were removed because the backend ignores them today.
  };

  const saveBookingSettings = async () => {
    if (!business?.businessId || !editingBookingStore) return;
    setSavingBooking(true);
    setBookingError("");
    try {
      const token = await getAuthToken();
      const url = `${awsConfig.api_url_get_menu}/${business.businessId}?action=update_booking_settings`;
      const body = {
        storeId: editingBookingStore.storeId || editingBookingStore.store_id,
        storeSlug: editingBookingStore.storeSlug || editingBookingStore.store_slug || editingBookingStore.slug,
        bookingSettings: bookingDraft,
      };
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update booking settings");
      }
      await fetchMenuPublishes();

      setEditingBookingStore(null);
    } catch (err) {
      console.error("Booking settings update failed", err);
      setBookingError(err?.message || "Could not update booking settings.");
    } finally {
      setSavingBooking(false);
    }
  };

  // ===== Individual Store Slug Edit =====
  const handleStoreSlugEdit = (publishedAt, currentSlug) => {
    setEditingStore(publishedAt);
    setNewStoreSlug(currentSlug || 'index');
  };

  const handleStoreSlugUpdate = async (publishedAt) => {
    if (!business?.businessId || !newStoreSlug.trim()) return;
    
    setSlugLoading(true);
    setSlugError('');
    
    try {
      const token = await getAuthToken();
      
      const res = await fetch(`${SLUG_API_URL}?action=store`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: business.businessId,
          publishedAt: publishedAt,
          newStoreSlug: newStoreSlug.trim().toLowerCase()
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update store slug');
      }
      
      // Update local state
      setMenuPublishes(prev => ({
        ...prev,
        active: prev.active.map(pub => 
          pub.publishedAt === publishedAt 
            ? { ...pub, storeSlug: data.newStoreSlug, htmlUrl: data.newHtmlUrl }
            : pub
        )
      }));
      
      setEditingStore(null);
      setNewStoreSlug('');
      
    } catch (err) {
      setSlugError(err.message);
    } finally {
      setSlugLoading(false);
    }
  };

  // ===== Handlers =====
  const handleSignOut = async () => {
    await signOut();
    if (onSignOut) onSignOut();
    navigate("/auth");
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const shareOnWhatsApp = (url) => {
    const text = encodeURIComponent(`Check out our menu! 🍽️ Order here: ${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  // ===== Monthly Usage Stats =====
  const { monthlyOrdersUsed, monthlyRevenueUsed } = calculateMonthlyUsage();
  
  const monthlyOrderLimit = business?.monthlyOrderLimit || 40;
  const monthlyRevenueLimit = Number(business?.monthlyRevenueLimit || 800);

  const orderUsagePercentage = Math.min((monthlyOrdersUsed / monthlyOrderLimit) * 100, 100);
  const revenueUsagePercentage = Math.min((monthlyRevenueUsed / monthlyRevenueLimit) * 100, 100);

  const isOrderLimitNear = orderUsagePercentage >= 80;
  const isRevenueLimitNear = revenueUsagePercentage >= 80;
  const isOrderLimitExceeded = monthlyOrdersUsed >= monthlyOrderLimit;
  const isRevenueLimitExceeded = monthlyRevenueUsed >= monthlyRevenueLimit;
  const hasExceededLimits = isOrderLimitExceeded || isRevenueLimitExceeded;

  const updateOrderStatus = async (orderId, newStatus) => {
    if (!business?.businessId) return;
    
    if (newStatus === "ACCEPTED" && (isOrderLimitExceeded || isRevenueLimitExceeded)) {
      setConfirmAction({ type: 'upgrade_required', orderId, newStatus });
      return;
    }

    try {
      setUpdatingOrderId(orderId);
      const token = await getAuthToken();
      const res = await fetch(awsConfig.api_url_get_order, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ businessId: business.businessId, orderId, status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed to update order");
      const data = await res.json();
      setOrders((prev) => prev.map((o) => (o.orderId === orderId ? data.order : o)));
    } catch (err) {
      console.error("Error updating order", err);
    } finally {
      setUpdatingOrderId(null);
      setConfirmAction(null);
    }
  };

  const updateLeadArchive = async (lead, archived) => {
    const targetId = lead?.orderId || lead?.leadId;
    if (!business?.businessId || !targetId) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(awsConfig.api_url_get_order, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          businessId: business.businessId,
          orderId: targetId,
          archived: Boolean(archived),
        }),
      });
      if (!res.ok) throw new Error("Failed to archive lead");
      const data = await res.json();
      setOrders((prev) =>
        prev.map((o) => (o.orderId === targetId ? data.order : o))
      );
    } catch (err) {
      console.error("Error archiving lead", err);
    }
  };

  const handleUpgrade = async (planIdOverride) => {
    const planId = planIdOverride || selectedPlanId;
    if (!planId) return;
    setUpgrading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(UPGRADE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: business.businessId,
          email: business.email,
          planId,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Could not start upgrade. Please try again.");
      }
    } catch {
      alert("Upgrade failed. Please try again.");
    } finally {
      setUpgrading(false);
    }
  };

  const handleContactUs = () => {
    window.open('mailto:info@brandmykitchen.com?subject=Enterprise Plan Inquiry', '_blank');
  };

  // ===== All-Time Stats =====
  const completedOrders = realOrders.filter(o =>
    ['ACCEPTED', 'PAID', 'COMPLETED'].includes(o.status)
  );
  
  const totalOrders = completedOrders.length;
  const totalRevenue = completedOrders.reduce(
    (sum, o) => sum + Number(o.total || 0), 0
  );

  const itemCounts = {};
  completedOrders.forEach((o) =>
    (o.items || []).forEach((i) => {
      itemCounts[i.name] = (itemCounts[i.name] || 0) + i.qty;
    })
  );
  const topItemName = Object.keys(itemCounts).sort((a, b) => itemCounts[b] - itemCounts[a])[0] || null;

  // ===== Pending Orders Counter =====
  const pendingOrders = realOrders.filter(o => PENDING_STATUSES.has(o.status));
  const pendingRevenue = pendingOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

  const renderActionIndicator = (status) => {
    if (status === "ACCEPTED") {
      return <span className="action-indicator success"><CheckCircle size={14} /> Completed</span>;
    }
    if (status === "PAID") {
      return <span className="action-indicator success">💳 Paid</span>;
    }
    if (status === "REJECTED") {
      return <span className="action-indicator muted">—</span>;
    }
    return null;
  };

  // ===== Get store display name =====
  const getStoreDisplayName = (storeSlug, storeId) => {
    if (!storeSlug && !storeId) return "Main Store";
    if (storeSlug === "index") return "Main Store";
    return storeSlug || storeId || "Main Store";
  };
  
  const orderConfirmTitleId =
    confirmAction?.type === 'order' && confirmAction?.order
      ? `order-confirm-${confirmAction.order.orderId}`
      : 'order-confirm-title';

  const storeDeleteTitleId = pendingStoreDeletion
    ? `store-delete-${pendingStoreDeletion.publishedAt}`
    : 'store-delete-title';

  // ===== Render =====
  return (
    <div className="home-page">
      <div className="home-container">
      <div className="home-logo-wrapper">
        {business?.logoUrl ? (
          <img src={business.logoUrl} alt="Business Logo" className="home-logo" />
        ) : (
        <img src={LogoImg} alt="Layerbound" className="home-logo" />
        )}
      </div>
      
      <div className="home-header">
        <div className="home-title">
          {loadingBusiness ? (
            <div className="loading-text">
              <Clock size={16} />
              Loading business...
            </div>
          ) : (
            <>
              <Sparkles size={20} className="title-icon" />
              Welcome {business?.name || ""}
            </>
          )}
        </div>

        {/* Plan Badge */}
        {!loadingBusiness && planBadge && (
          <div className="plan-indicator">
            <span className={planBadge.className}>
              {planBadge.icon}
              {planBadge.label}
            </span>
            {activePlan?.keyPoints?.length ? (
              <ul className="plan-feature-list">
                {activePlan.keyPoints.slice(0, 3).map((point) => (
                  <li key={point}>
                    <CheckCircle size={12} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>

      {/* Monthly Usage Stats */}
      {!loadingBusiness && business && (
        <div className="usage-stats-section">
          <div className="stats-header">
            <h3 className="usage-stats-title">
              <TrendingUp size={20} />
              Monthly Usage
            </h3>
            <button 
              className="btn btn-icon" 
              onClick={() => setShowAllTimeStats(!showAllTimeStats)}
              title="View all-time statistics"
            >
              <BarChart3 size={14} />
              {showAllTimeStats ? 'Hide Stats' : 'View Stats'}
            </button>
          </div>
          
          <div className="usage-stats">
            <div className="usage-stat">
              <div className="usage-stat-header">
                <Package size={16} />
                <span>Orders</span>
              </div>
              <div className="usage-stat-content">
                <div className="usage-numbers">
                  <span className={`usage-current ${isOrderLimitNear || isOrderLimitExceeded ? 'warning' : ''}`}>
                    {monthlyOrdersUsed}
                  </span>
                  <span className="usage-separator">/</span>
                  <span className="usage-limit">{monthlyOrderLimit}</span>
                </div>
                <div className="usage-bar-container">
                  <div className="usage-bar">
                    <div 
                      className={`usage-fill ${isOrderLimitNear || isOrderLimitExceeded ? 'warning' : ''} ${isOrderLimitExceeded ? 'exceeded' : ''}`}
                      style={{ width: `${orderUsagePercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="usage-percentage">
                  {orderUsagePercentage.toFixed(0)}% used
                </div>
              </div>
            </div>

            <div className="usage-stat">
              <div className="usage-stat-header">
                <TrendingUp size={16} />
                <span>Revenue</span>
              </div>
              <div className="usage-stat-content">
                <div className="usage-numbers">
                  <span className={`usage-current ${isRevenueLimitNear || isRevenueLimitExceeded ? 'warning' : ''}`}>
                    {formatAmount(monthlyRevenueUsed)}
                  </span>
                  <span className="usage-separator">/</span>
                  <span className="usage-limit">{formatAmount(monthlyRevenueLimit)}</span>
                </div>
                <div className="usage-bar-container">
                  <div className="usage-bar">
                    <div 
                      className={`usage-fill ${isRevenueLimitNear || isRevenueLimitExceeded ? 'warning' : ''} ${isRevenueLimitExceeded ? 'exceeded' : ''}`}
                      style={{ width: `${revenueUsagePercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="usage-percentage">
                  {revenueUsagePercentage.toFixed(0)}% used
                </div>
              </div>
            </div>
          </div>

          {/* All-Time Stats (Expandable) */}
          {showAllTimeStats && (
            <div className="all-time-content">
              <h4 className="all-time-title">All-Time Performance</h4>
              <div className="all-time-kpis">
                <div className="kpi small">
                  <div className="kpi-value">{formatAmount(totalRevenue)}</div>
                  <div className="kpi-label">Total Revenue</div>
                </div>
                <div className="kpi small">
                  <div className="kpi-value">{loadingOrders ? "…" : totalOrders}</div>
                  <div className="kpi-label">Total Orders</div>
                </div>
                <div className="kpi small">
                  <div className="kpi-value">{topItemName ? "🏆 " + topItemName : "—"}</div>
                  <div className="kpi-label">Most Ordered</div>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Usage Warning */}
          {(isOrderLimitNear || isRevenueLimitNear || hasExceededLimits) && (
            <div className={`usage-warning ${hasExceededLimits ? 'exceeded' : ''}`}>
              {hasExceededLimits ? (
                <>
                  <div className="warning-header">
                    <AlertTriangle size={18} />
                    <strong>Monthly limits exceeded!</strong>
                  </div>
                  <div className="warning-details">
                    <div className="warning-metrics">
                      {isOrderLimitExceeded && <div className="warning-metric">Orders: <span>{monthlyOrdersUsed}/{monthlyOrderLimit}</span> (100%)</div>}
                      {isRevenueLimitExceeded && <div className="warning-metric">Revenue: <span>{formatAmount(monthlyRevenueUsed)}/{formatAmount(monthlyRevenueLimit)}</span> (100%)</div>}
                    </div>
                    <div className="warning-message">
                      {pendingOrders.length > 0 ? (
                        <>
                          You have <strong>{pendingOrders.length} pending orders</strong> worth <strong>{formatAmount(pendingRevenue)}</strong> waiting.
                          <br />
                          {business?.plan === 'starter' ? (
                            "You've reached your Starter plan limits. Limits reset monthly, or contact us for Enterprise options."
                          ) : (
                            "You cannot accept new orders until you upgrade your plan or wait for next month."
                          )}
                        </>
                      ) : (
                        business?.plan === 'starter' 
                          ? "You've reached your Starter plan limits. Limits reset monthly."
                          : "You cannot accept new orders until you upgrade your plan or wait for next month."
                      )}
                    </div>
                  </div>
                  
                  <div className="upgrade-cta">
                    {business?.plan === 'starter' ? (
                      <button 
                        className="btn btn-secondary" 
                        onClick={handleContactUs}
                      >
                        Contact Us for Enterprise
                      </button>
                    ) : (
                      <button 
                        className="btn btn-primary" 
                        onClick={() => setConfirmAction({ type: "upgrade_required", source: "limit_exceeded" })}
                      >
                        <Zap size={16} />
                        View Plans
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="warning-header">
                    <AlertTriangle size={18} />
                    <span>Approaching monthly limits</span>
                  </div>
                  <div className="warning-details">
                    <div className="warning-metrics">
                      {isOrderLimitNear && <div className="warning-metric">Orders: <span>{monthlyOrdersUsed}/{monthlyOrderLimit}</span> ({orderUsagePercentage.toFixed(0)}%)</div>}
                      {isRevenueLimitNear && <div className="warning-metric">Revenue: <span>{formatAmount(monthlyRevenueUsed)}/{formatAmount(monthlyRevenueLimit)}</span> ({revenueUsagePercentage.toFixed(0)}%)</div>}
                    </div>
                  </div>
                  
                  <div className="upgrade-cta">
                    {business?.plan === 'starter' ? (
                      <button 
                        className="btn btn-outline" 
                        onClick={handleContactUs}
                      >
                        Contact Us for Enterprise
                      </button>
                    ) : (
                      <button 
                        className="btn btn-outline" 
                        onClick={() => setConfirmAction({ type: "upgrade_required", source: "limit_warning" })}
                      >
                        View Plans
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Orders */}
      <div className="orders-section">
        <div className="section-header">
          <h3 className="section-title">
            <Package size={18} />
            Orders
          </h3>
          <div className="section-actions">
            <select
              value={orderFilter.store}
              onChange={(e) => setOrderFilter((prev) => ({ ...prev, store: e.target.value }))}
            >
              <option value="all">All stores</option>
              {(menuPublishes.active || []).map((pub) => (
                <option key={pub.storeId || pub.storeSlug} value={pub.storeId || pub.storeSlug}>
                  {pub.storeName || pub.name || pub.storeSlug || pub.storeId}
                </option>
              ))}
              <option value="unknown">Unknown</option>
            </select>
          </div>
        </div>
        
        {loadingOrders ? (
          <div className="loading-state">
            <Clock size={20} />
            <span>Loading orders…</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="empty-state">
            <Package size={32} />
            <p>No orders yet</p>
            <span>Share your link to start receiving orders 🚀</span>
          </div>
        ) : (
          <div className="orders-table-wrapper leads-table-wrapper">
            <div className="orders-table-scroll leads-table-scroll">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Store</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const isPending = PENDING_STATUSES.has(o.status);
                    const canAcceptOrders = !hasExceededLimits;
                    const storeMeta = resolveStoreMeta(o);
                    const bookingDetails = formatBookingDetails(o);
                    const itemList = formatItemsList(o);
                    
                    return (
                      <tr key={o.orderId}>
                        <td>{o.orderId?.slice(-5)}</td>
                        <td>{o.customerName}</td>
                        <td>{o.customerPhone}</td>
                        <td>{o.customerAddress || "—"}</td>
                        <td>
                          <div className="order-items-cell">
                            <div className="order-items-list">{itemList}</div>
                            {bookingDetails.length > 0 && (
                              <div className="order-booking-tags">
                                {bookingDetails.map((booking) => (
                                  <span key={booking.id} className="order-booking-tag">
                                    {booking.text}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      <td>{formatAmount(o.total)}</td>
                      <td>
                        {storeMeta.url ? (
                          <div className="store-link-cell">
                            <a href={storeMeta.url} target="_blank" rel="noreferrer">
                              {storeMeta.name}
                            </a>
                            <button
                              type="button"
                              className="copy-link-btn"
                              onClick={() => navigator.clipboard?.writeText(storeMeta.url)}
                              title="Copy store link"
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          storeMeta.name
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${o.status.toLowerCase()}`}>{o.status}</span>
                      </td>
                      <td className={isPending ? "actions-cell" : "actions-cell-empty"}>
                        {isPending ? (
                          <>
                            <button
                              type="button"
                              className={`order-action-btn ${updatingOrderId === o.orderId || !canAcceptOrders ? "disabled" : ""}`}
                              disabled={updatingOrderId === o.orderId || !canAcceptOrders}
                              onClick={() =>
                                setConfirmAction({ type: "order", orderId: o.orderId, newStatus: "ACCEPTED", order: o })
                              }
                              title={!canAcceptOrders ? "Upgrade required to accept orders" : ""}
                            >
                              {!canAcceptOrders ? "🔒 Accept" : "Accept"}
                            </button>
                            <button
                              type="button"
                              className={`order-action-btn subtle ${updatingOrderId === o.orderId ? "disabled" : ""}`}
                              disabled={updatingOrderId === o.orderId}
                              onClick={() =>
                                setConfirmAction({ type: "order", orderId: o.orderId, newStatus: "REJECTED", order: o })
                              }
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          renderActionIndicator(o.status)
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="orders-cards">
                {filteredOrders.map((o) => {
                  const isPending = PENDING_STATUSES.has(o.status);
                  const canAcceptOrders = !hasExceededLimits;
                  const bookingDetails = formatBookingDetails(o);
                  const itemList = formatItemsList(o);
                
                return (
                  <div key={o.orderId} className="order-card">
                      <div><strong>Order ID:</strong> {o.orderId?.slice(-5)}</div>
                    <div><strong>Customer:</strong> {o.customerName}</div>
                    <div><strong>Phone:</strong> {o.customerPhone}</div>
                    <div><strong>Address:</strong> {o.customerAddress || "—"}</div>
                    <div className="order-items-cell">
                      <div><strong>Items:</strong> {itemList}</div>
                      {bookingDetails.length > 0 && (
                        <div className="order-booking-tags">
                          {bookingDetails.map((booking) => (
                            <span key={booking.id} className="order-booking-tag">
                              {booking.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div><strong>Total:</strong> {formatAmount(o.total)}</div>
                    <div><strong>Status:</strong> <span className={`status-badge ${o.status.toLowerCase()}`}>{o.status}</span></div>
                    <div className="order-card-actions">
                      {isPending ? (
                        <div className="order-actions-inline">
                          <button
                            type="button"
                            className={`order-action-btn ${updatingOrderId === o.orderId || !canAcceptOrders ? "disabled" : ""}`}
                            disabled={updatingOrderId === o.orderId || !canAcceptOrders}
                            onClick={() =>
                              setConfirmAction({ type: "order", orderId: o.orderId, newStatus: "ACCEPTED", order: o })
                            }
                            title={!canAcceptOrders ? "Upgrade required to accept orders" : ""}
                          >
                            {!canAcceptOrders ? "🔒 Accept" : "Accept"}
                          </button>
                          <button
                            type="button"
                            className={`order-action-btn subtle ${updatingOrderId === o.orderId ? "disabled" : ""}`}
                            disabled={updatingOrderId === o.orderId}
                            onClick={() =>
                              setConfirmAction({ type: "order", orderId: o.orderId, newStatus: "REJECTED", order: o })
                            }
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        renderActionIndicator(o.status)
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Candidates */}
      <div className="orders-section">
        <div className="section-header">
          <h3 className="section-title">
            <MessageCircle size={18} />
            Candidates
          </h3>
          <div className="section-actions">
            <select
              value={leadFilter.store}
              onChange={(e) => setLeadFilter((prev) => ({ ...prev, store: e.target.value }))}
            >
              <option value="all">All stores</option>
              {(menuPublishes.active || []).map((pub) => (
                <option key={pub.storeId || pub.storeSlug} value={pub.storeId || pub.storeSlug}>
                  {pub.storeName || pub.name || pub.storeSlug || pub.storeId}
                </option>
              ))}
              <option value="unknown">Unknown</option>
            </select>
            <select
              value={leadFilter.source}
              onChange={(e) => setLeadFilter((prev) => ({ ...prev, source: e.target.value }))}
            >
              <option value="all">All sources</option>
              <option value="storefront">Storefront</option>
              <option value="api">API</option>
              <option value="unknown">Unknown</option>
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowArchivedLeads((prev) => !prev)}
            >
              {showArchivedLeads ? "Hide archived" : "Show archived"}
            </button>
            {leads.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const isIntakeLead = (lead) => {
                    const leadPayload = lead?.lead || lead || {};
                    const mode =
                      leadPayload.recruitment_mode ||
                      leadPayload.recruitmentMode ||
                      lead.recruitment_mode ||
                      lead.recruitmentMode ||
                      "";
                    const template = String(leadPayload.template || lead.template || "").toLowerCase();
                    return String(mode).toLowerCase() === "intake" || template.includes("intake");
                  };
                  const getIntakeSummary = (lead) => {
                    const drr = resolveDrr(lead);
                    const profile = drr?.intake_profile || {};
                    const summaryParts = [
                      profile.route,
                      profile.company ? `Company: ${profile.company}` : "",
                      profile.people_count ? `People: ${profile.people_count}` : "",
                      profile.start_timing ? `Start: ${profile.start_timing}` : "",
                      profile.preferred_contact ? `Contact: ${profile.preferred_contact}` : "",
                      profile.enquiry_details ? `Details: ${profile.enquiry_details}` : "",
                    ].filter(Boolean);
                    const missing = Array.isArray(profile.missing_info)
                      ? profile.missing_info.filter((item) => String(item || "").trim())
                      : [];
                    return {
                      route: profile.route || "",
                      company: profile.company || "",
                      peopleCount: profile.people_count || "",
                      startTiming: profile.start_timing || "",
                      preferredContact: profile.preferred_contact || "",
                      summary: summaryParts.join(" | ") || "—",
                      missing: missing.join(" | "),
                    };
                  };
                  const rows = [
                    [
                      "Name",
                      "Email",
                      "Phone",
                      "Route",
                      "Company",
                      "People Count",
                      "Start Timing",
                      "Preferred Contact",
                      "Summary",
                      "Missing Info",
                      "Store",
                      "Store URL",
                      "Source",
                      "Created",
                    ],
                    ...filteredLeads.map((lead) => {
                      const storeMeta = resolveStoreMeta(lead);
                      const email = lead.lead?.email || lead.customerEmail || lead.email;
                      const phone = lead.lead?.phone || lead.customerPhone || lead.phone;
                      const name = lead.lead?.name || lead.customerName || lead.name || "Submission";
                      const source = (lead.lead?.source || lead.source || "storefront").toLowerCase();
                      const created = lead.createdAt || "";
                      if (isIntakeLead(lead)) {
                        const intake = getIntakeSummary(lead);
                        return [
                          name,
                          email,
                          phone,
                          intake.route,
                          intake.company,
                          intake.peopleCount,
                          intake.startTiming,
                          intake.preferredContact,
                          intake.summary,
                          intake.missing,
                          storeMeta.name,
                          storeMeta.url,
                          source,
                          created,
                        ];
                      }
                      const goal = lead.lead?.goal || lead.goal || "";
                      const notes = lead.lead?.notes || lead.notes || "";
                      return [
                        name,
                        email,
                        phone,
                        "",
                        "",
                        "",
                        "",
                        "",
                        [goal, notes].filter(Boolean).join(" | ") || "—",
                        "",
                        storeMeta.name,
                        storeMeta.url,
                        source,
                        created,
                      ];
                    }),
                  ];
                  const csv = rows.map((r) => r.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute("download", "intake-submissions.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
              >
                Export CSV
              </button>
            )}
          </div>
        </div>
        {loadingOrders ? (
          <div className="loading-state">
            <Clock size={20} />
            <span>Loading submissions…</span>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="empty-state">
            <MessageCircle size={32} />
            <p>No submissions yet</p>
            <span>Completed intake links and enquiry submissions will appear here.</span>
          </div>
        ) : (
          <div className="orders-table-wrapper">
            <div className="orders-table-scroll">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Intake summary</th>
                    <th>Store</th>
                    <th>Source</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => {
                    const storeMeta = resolveStoreMeta(lead);
                    const drr = resolveDrr(lead);
                    const intakeProfile = drr?.intake_profile || null;
                    const created = lead.createdAt ? new Date(lead.createdAt).toLocaleString() : "—";
                    const goal = lead.lead?.goal || lead.goal || "";
                    const notes = lead.lead?.notes || lead.notes || "";
                    const intakeSummary = intakeProfile
                      ? [
                          intakeProfile.route,
                          intakeProfile.company ? `Company: ${intakeProfile.company}` : "",
                          intakeProfile.people_count ? `People: ${intakeProfile.people_count}` : "",
                          intakeProfile.start_timing ? `Start: ${intakeProfile.start_timing}` : "",
                          intakeProfile.preferred_contact ? `Contact: ${intakeProfile.preferred_contact}` : "",
                          intakeProfile.enquiry_details ? `Details: ${intakeProfile.enquiry_details}` : "",
                          Array.isArray(intakeProfile.missing_info) && intakeProfile.missing_info.length
                            ? `Missing: ${intakeProfile.missing_info.join(", ")}`
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" • ")
                      : "";
                    const summary = intakeSummary || [goal, notes].filter(Boolean).join(" • ") || "—";
                    const email = lead.lead?.email || lead.customerEmail || lead.email;
                    const phone = lead.lead?.phone || lead.customerPhone || lead.phone;
                    const name = lead.lead?.name || lead.customerName || lead.name || "Submission";
                    const source = lead.lead?.source || lead.source || "storefront";
                    const isArchived = Boolean(lead.archived || lead.lead?.archived);
                    return (
                      <tr key={lead.orderId || lead.leadId}>
                        <td>
                          <span>{name}</span>
                          {showArchivedLeads && isArchived ? (
                            <span className="archived-badge">Archived</span>
                          ) : null}
                        </td>
                        <td><a href={`mailto:${email}`}>{email}</a></td>
                        <td>{phone || "—"}</td>
                        <td>{summary}</td>
                    <td>
                      {storeMeta.url ? (
                        <div className="store-link-cell">
                          <a href={storeMeta.url} target="_blank" rel="noreferrer">
                            {storeMeta.name}
                          </a>
                          <button
                            type="button"
                            className="copy-link-btn"
                            onClick={() => navigator.clipboard?.writeText(storeMeta.url)}
                            title="Copy store link"
                          >
                            Copy
                          </button>
                        </div>
                      ) : (
                        storeMeta.name
                      )}
                    </td>
                        <td>{source}</td>
                        <td>{created}</td>
                        <td>
                          <button
                            type="button"
                            className="copy-link-btn"
                            onClick={() => updateLeadArchive(lead, !isArchived)}
                          >
                            {isArchived ? "Unarchive" : "Archive"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="modal-overlay">
          <div
            className="modal-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby={
              confirmAction.type === 'upgrade_required'
                ? 'upgrade-required-title'
                : orderConfirmTitleId
            }
          >
            <button
              type="button"
              className="modal-close-button"
              onClick={() => setConfirmAction(null)}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
            
            {confirmAction.type === 'upgrade_required' ? (
              <>
                <div className="upgrade-required-modal">
                  <div className="upgrade-icon">
                    <AlertTriangle size={32} color="#dc2626" />
                  </div>
                  <h3 id="upgrade-required-title" className="modal-title">
                    {saleWindow.phase === "active" ? "Lifetime Deal is live" : "Upgrade required"}
                  </h3>
                  <p className="modal-message">
                    {business?.plan === "starter"
                      ? "You've reached your Starter plan limits. Get in touch for Enterprise scale."
                      : saleStatusCopy}
                  </p>
                  <div className="limit-details">
                    <div className="limit-item">
                      Orders: <strong>{monthlyOrdersUsed}/{monthlyOrderLimit}</strong>
                    </div>
                    <div className="limit-item">
                      Revenue: <strong>{formatAmount(monthlyRevenueUsed)}/{formatAmount(monthlyRevenueLimit)}</strong>
                    </div>
                  </div>
                  {pendingOrders.length > 0 && (
                    <div className="pending-orders-alert">
                      <strong>⏳ {pendingOrders.length} orders waiting</strong> ({formatAmount(pendingRevenue)} potential revenue)
                    </div>
                  )}
                  {business?.plan !== "starter" ? (
                    <>
                      <UpgradePlanSelector
                        className="modal-plan-selector"
                        plans={availablePlans}
                        selectedPlanId={selectedPlanId}
                        onSelect={setSelectedPlanId}
                        salePhase={saleWindow.phase}
                        countdownLabel={countdownLabel}
                      />
                      {selectedPlan?.keyPoints?.length > 0 && (
                        <ul className="upgrade-benefits">
                          {selectedPlan.keyPoints.map((point) => (
                            <li key={point}>{point}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : null}
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="modal-button modal-button--ghost"
                    onClick={() => setConfirmAction(null)}
                  >
                    Cancel
                  </button>
                  {business?.plan === 'starter' ? (
                    <button
                      type="button"
                      className="modal-button modal-button--primary"
                      onClick={handleContactUs}
                    >
                      Contact Us
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="modal-button modal-button--primary"
                      onClick={() => handleUpgrade(selectedPlanId)}
                      disabled={upgrading}
                    >
                      <Zap size={16} />
                      {upgrading ? "Processing..." : selectedPlan?.ctaLabel || "Upgrade Now"}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <h3 id={orderConfirmTitleId} className="modal-title">
                  {confirmAction.newStatus === "ACCEPTED" ? "Accept this order?" : "Reject this order?"}
                </h3>
                {confirmAction.order && (
                  <div className="modal-order-summary">
                    <div className="modal-order-summary-row">
                      <span>Order ID</span>
                      <strong>#{confirmAction.order.orderId}</strong>
                    </div>
                    <div className="modal-order-summary-row">
                      <span>Customer</span>
                      <strong>{confirmAction.order.customerName || "Guest"}</strong>
                    </div>
                    {confirmAction.order.customerPhone && (
                      <div className="modal-order-summary-row">
                        <span>Phone</span>
                        <strong>{confirmAction.order.customerPhone}</strong>
                      </div>
                    )}
                    {confirmAction.order.customerAddress && (
                      <div className="modal-order-summary-row">
                        <span>Address</span>
                        <strong>{confirmAction.order.customerAddress}</strong>
                      </div>
                    )}
                    <div className="modal-order-summary-row">
                      <span>Total</span>
                      <strong>{formatAmount(confirmAction.order.total)}</strong>
                    </div>
                    {Array.isArray(confirmAction.order.items) && confirmAction.order.items.length > 0 && (
                      <div className="modal-order-items">
                        <span>Items</span>
                        <ul>
                          {confirmAction.order.items.slice(0, 3).map((item, idx) => (
                            <li key={`${confirmAction.order.orderId}-${idx}`}>
                              {item.name} ×{item.qty}
                            </li>
                          ))}
                        </ul>
                        {confirmAction.order.items.length > 3 && (
                          <div className="modal-order-items-more">
                            +{confirmAction.order.items.length - 3} more item{confirmAction.order.items.length - 3 === 1 ? "" : "s"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="modal-message">
                  Are you sure you want to{" "}
                  <strong>{confirmAction.newStatus === "ACCEPTED" ? "accept" : "reject"}</strong>{" "}
                  this order?
                </p>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="modal-button modal-button--ghost"
                    onClick={() => setConfirmAction(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`modal-button ${
                      confirmAction.newStatus === "ACCEPTED"
                        ? "modal-button--primary"
                        : "modal-button--danger"
                    }`}
                    onClick={() => updateOrderStatus(confirmAction.orderId, confirmAction.newStatus)}
                    disabled={updatingOrderId === confirmAction.orderId}
                  >
                    {updatingOrderId === confirmAction.orderId
                      ? confirmAction.newStatus === "ACCEPTED"
                        ? "Accepting..."
                        : "Rejecting..."
                      : confirmAction.newStatus === "ACCEPTED"
                      ? "Yes, accept"
                      : "Yes, reject"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editingBookingStore && (
        <div className="modal-overlay">
          <div
            className="modal-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-settings-title"
          >
            <button
              type="button"
              className="modal-close-button"
              onClick={() => setEditingBookingStore(null)}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
            <h3 id="booking-settings-title" className="modal-title">Booking settings</h3>
            <p className="modal-message">
              These settings apply to{" "}
              <strong>
                {getStoreDisplayName(
                  editingBookingStore.storeSlug || editingBookingStore.store_slug,
                  editingBookingStore.storeId || editingBookingStore.store_id
                )}
              </strong>.
            </p>
            <div className="modal-form">
              <label className="modal-form-field">
                <span>Resource label</span>
                <input
                  type="text"
                  value={bookingDraft.resourceLabel}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({ ...prev, resourceLabel: e.target.value }))
                  }
                  placeholder="Coach name or room name"
                />
              </label>
              <label className="modal-form-field">
                <span>Start time (HH:MM)</span>
                <input
                  type="time"
                  value={bookingDraft.startTime}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                />
              </label>
              <label className="modal-form-field">
                <span>End time (HH:MM)</span>
                <input
                  type="time"
                  value={bookingDraft.endTime}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                />
              </label>
              <label className="modal-form-field">
                <span>Slot duration (minutes)</span>
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={bookingDraft.slotDurationMinutes}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({
                      ...prev,
                      slotDurationMinutes: Number(e.target.value) || 0,
                    }))
                  }
                />
              </label>
              <label className="modal-form-field">
                <span>Max simultaneous bookings</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={bookingDraft.maxSimultaneousBookings}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({
                      ...prev,
                      maxSimultaneousBookings: Number(e.target.value) || 1,
                    }))
                  }
                />
              </label>
              <label className="modal-form-field">
                <span>Timezone (e.g. Europe/London)</span>
                <input
                  type="text"
                  value={bookingDraft.timezone}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({ ...prev, timezone: e.target.value }))
                  }
                  placeholder="UTC"
                />
              </label>
              <label className="modal-form-field">
                <span>Days of week (0=Mon ... 6=Sun, comma separated)</span>
                <input
                  type="text"
                  value={(bookingDraft.daysOfWeek || []).join(",")}
                  onChange={(e) => {
                    const raw = e.target.value
                      .split(",")
                      .map((v) => Number(v.trim()))
                      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
                    setBookingDraft((prev) => ({ ...prev, daysOfWeek: raw.length ? raw : [] }));
                  }}
                  placeholder="0,1,2,3,4,5,6"
                />
              </label>
              <label className="modal-form-field">
                <span>Notes (optional, shown near calendar)</span>
                <textarea
                  rows="3"
                  value={bookingDraft.notes}
                  onChange={(e) =>
                    setBookingDraft((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="E.g. Zoom only on Mondays, bring gym shoes, etc."
                />
              </label>
            </div>

            <div className="modal-helper">
              Multi-schedule and override fields are disabled until backend support is added. Use the basic availability
              settings above for now.
            </div>
            {bookingError && <div className="modal-error">{bookingError}</div>}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-button modal-button--ghost"
                onClick={() => setEditingBookingStore(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button modal-button--primary"
                onClick={saveBookingSettings}
                disabled={savingBooking}
              >
                {savingBooking ? "Saving..." : "Save settings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingStoreDeletion && (
        <div className="modal-overlay">
          <div
            className="modal-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby={storeDeleteTitleId}
          >
            <button
              type="button"
              className="modal-close-button"
              onClick={() => {
                setPendingStoreDeletion(null);
                setDeleteStoreError('');
              }}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
            <div className="modal-header-icon danger">
              <Trash2 size={26} />
            </div>
            <h3 id={storeDeleteTitleId} className="modal-title">Delete this menu?</h3>
            <p className="modal-message">
              Removing <strong>{pendingStoreDeletion.storeName}</strong> will delete the published menu link.
            </p>
            {deleteStoreError && (
              <div className="modal-error">
                {deleteStoreError}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-button modal-button--ghost"
                onClick={() => {
                  setPendingStoreDeletion(null);
                  setDeleteStoreError('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button modal-button--danger"
                onClick={confirmStoreDeletion}
                disabled={deletingPublish === pendingStoreDeletion.publishedAt}
              >
                {deletingPublish === pendingStoreDeletion.publishedAt ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <CallReadinessModal
        isOpen={showCallReadiness}
        onClose={() => setShowCallReadiness(false)}
        callReadyLeads={callReadyLeads}
        callReadyStats={callReadyStats}
        activeStatusFilter={callReadinessFilter}
        onStatusFilterChange={setCallReadinessFilter}
        resolveDrr={resolveDrr}
        resolveStoreMeta={resolveStoreMeta}
        buildClientSummary={buildClientSummary}
        buildCandidateSummary={buildCandidateSummary}
        handleCopySummary={handleCopySummary}
        copiedSummaryId={copiedSummaryId}
        onArchiveLead={updateLeadArchive}
      />

      {/* Actions */}
      <div className="home-actions">
        <Link className="btn btn-primary" to="/systems">
          <Sparkles size={16} />
          Create System
        </Link>
        {business?.plan === "starter" && (
          <Link className="btn btn-outline" to="/systems/used-car">
            <Zap size={16} />
            Create car-check link
          </Link>
        )}
        <Link className="btn btn-outline" to="/systems/gatecheck">
          <AlertTriangle size={16} />
          Create Gatecheck link
        </Link>
        <Link className="btn btn-outline" to="/systems/collaboration-milestone-gatecheck">
          <AlertTriangle size={16} />
          Create milestone gatecheck
        </Link>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => setShowCallReadiness(true)}
        >
          <CheckCircle size={16} />
          Call Readiness
        </button>
        <Link className="btn btn-outline" to="/settings">Settings</Link>
        <Link className="btn btn-outline" to="/integrations">
          <KeyRound size={16} />
          Integrations
        </Link>
        {!paymentsEnabled && (
          <Link className="btn btn-text" to="/settings">Connect Stripe</Link>
        )}
        <button
          className="btn btn-link"
          onClick={() => window.open("https://www.brandmykitchen.com/how-it-works", "_blank")}
        >
          How It Works?
        </button>
      </div>

      {/* Multi-Store Menu Links Section with Slug Management */}
      <div className="share-card">
        <div className="share-header">
          <h4 className="share-title">
            <Store size={18} />
            Your Menu Links
            {business?.plan === 'starter' && menuPublishes.active.length > 1 && (
              <span className="multi-store-badge">Multi-Store</span>
            )}
          </h4>
          <button 
            className="btn btn-icon-sm"
            onClick={() => setShowSlugEdit(!showSlugEdit)}
            title="Edit your custom URL"
          >
            <Edit3 size={14} />
            Edit URL
          </button>
        </div>

        {showSlugEdit && (
          <div className="slug-editor">
            <div className="slug-input-group">
              <span className="slug-prefix">{MENU_BASE_DISPLAY}/</span>
              <input
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="your-restaurant-name"
                className="slug-input"
                maxLength={50}
              />
            </div>
            {slugError && <div className="slug-error">{slugError}</div>}
            <div className="slug-actions">
              <button 
                className="btn btn-outline"
                onClick={() => {
                  setShowSlugEdit(false);
                  setNewSlug(derivedBusinessSlug);
                  setSlugError('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSlugUpdate}
                disabled={slugLoading || !newSlug.trim() || newSlug === business?.slug}
              >
                {slugLoading ? "Updating all stores..." : "Save & Update All"}
              </button>
            </div>
            <div className="slug-help">
              This will update URLs for all your stores. Use lowercase letters, numbers, and hyphens only.
            </div>
          </div>
        )}

        {menuPublishes.active.length === 0 ? (
          <div className="empty-links">
            <ExternalLink size={20} />
            <span>No published menus yet. Create your first menu to get started!</span>
          </div>
        ) : (
          <div className="store-links">
            {menuPublishes.active.map((pub) => {
              const storeDisplayName = getStoreDisplayName(pub.storeSlug, pub.storeId);
              const isDeleting = deletingPublish === pub.publishedAt;
              const isEditingThisStore = editingStore === pub.publishedAt;
              const normalizedStoreSlug = pub.storeSlug && pub.storeSlug !== "index" ? pub.storeSlug : "index";
              const storeUrl = `${MENU_BASE_URL}/${derivedBusinessSlug}/${normalizedStoreSlug}`;
              const templateName = String(pub.template || "").toLowerCase();
              const templateLabel =
                templateName === "used_car_decision"
                  ? "Car-check flow"
                  : templateName === "gatecheck"
                    ? "Gatecheck link"
                    : templateName === "collaboration_milestone_gatecheck"
                      ? "Milestone gatecheck link"
                    : templateName === "recruitment"
                      ? "Intake flow"
                      : "Published link";
              
              return (
                <div key={pub.publishedAt} className="store-link-card">
                  <div className="store-info">
                    <div className="store-name">
                      <Store size={14} />
                      <span>{storeDisplayName}</span>
                      {business?.plan === 'starter' && (
                        <button 
                          className="btn btn-icon-xs"
                          onClick={() => handleStoreSlugEdit(pub.publishedAt, pub.storeSlug)}
                          title="Edit this store's URL"
                        >
                          <Edit3 size={12} />
                        </button>
                      )}
                    </div>
                    <div className="store-details">
                      {templateLabel} • {pub.itemsCount} items • Published {new Date(pub.publishedAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  {isEditingThisStore ? (
                    <div className="store-slug-editor">
                      <div className="store-slug-input-group">
                        <span className="slug-prefix">{MENU_BASE_DISPLAY}/{derivedBusinessSlug}/</span>
                        <input
                          type="text"
                          value={newStoreSlug}
                          onChange={(e) => setNewStoreSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                          placeholder="store-name"
                          className="store-slug-input"
                          maxLength={30}
                        />
                      </div>
                      {slugError && <div className="slug-error">{slugError}</div>}
                      <div className="store-slug-actions">
                        <button 
                          className="btn btn-outline"
                          onClick={() => {
                            setEditingStore(null);
                            setNewStoreSlug('');
                            setSlugError('');
                          }}
                        >
                          Cancel
                        </button>
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleStoreSlugUpdate(pub.publishedAt)}
                          disabled={slugLoading || !newStoreSlug.trim()}
                        >
                          {slugLoading ? "Updating..." : "Update"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="store-url">
                      <a
                        href={storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="menu-link"
                        title={storeUrl}
                      >
                        <ExternalLink size={14} />
                        {`${derivedBusinessSlug}/${normalizedStoreSlug}`}
                      </a>
                    </div>
                  )}
                  
                  {!isEditingThisStore && (
                    <div className="store-actions">
                      <button
                        className="btn btn-icon-xs"
                        onClick={() => openBookingModal(pub)}
                        title="Booking settings"
                      >
                        <Clock size={14} />
                      </button>
                      <button 
                        className="btn btn-icon-xs"
                        onClick={() => copyToClipboard(storeUrl)}
                        title="Copy link"
                      >
                        {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
                      </button>
                      <button 
                        className="btn btn-icon-xs"
                        onClick={() => shareOnWhatsApp(storeUrl)}
                        title="Share on WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </button>
                      <Link 
                        className="btn btn-icon-xs"
                        to="/qr"
                        state={{ url: storeUrl, storeName: storeDisplayName }}
                        title="Generate QR code"
                      >
                        <QrCode size={14} />
                      </Link>
                      <button 
                        className="btn btn-icon-xs danger"
                        onClick={() => {
                          setDeleteStoreError('');
                          setPendingStoreDeletion({
                            publishedAt: pub.publishedAt,
                            storeName: storeDisplayName,
                            storeSlug: pub.storeSlug,
                            url: storeUrl
                          });
                        }}
                        disabled={isDeleting}
                        title="Delete this menu"
                      >
                        {isDeleting ? <Clock size={14} /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Plan indicator for stores */}
        {business?.plan === 'free' && menuPublishes.active.length > 0 && (
          <div className="store-plan-notice">
            <AlertTriangle size={14} />
            Upgrade to Starter plan to create multiple ordering menus (up to 3 menus)
          </div>
        )}
      </div>

      <button className="btn btn-text sign-out-btn" onClick={handleSignOut}>
        Sign Out
      </button>

      <div className="home-footer">
        <div className="footer-content">
          © {new Date().getFullYear()} Layerbound. All rights reserved.
        </div>
      </div>
      </div>
    </div>
  );
}

export default HomePage;
